import { flatten, groupBy } from 'lodash';

import helper from './completion-providers/helper';

export function configureCustomizeCompletion(ace: AceAjax.Ace): (e) => void {
  function configureDocTooltipTheme(autoComplete: any) {
    const { showDocTooltip: existingShowDocTooltipFn } = autoComplete;
    // This is the only modification we need;
    return Object.assign(autoComplete, {
      _docToolTipTheme: 'ace_doc-tooltip-light',
      updateDocTooltipTheme(value) {
        if (!this._docToolTipTheme.endsWith(value)) {
          this._docToolTipTheme = `ace_doc-tooltip-${value}`;
        }
      },
      showDocTooltip(item) {
        existingShowDocTooltipFn.call(autoComplete, item);
        this.tooltipNode.style.display = 'grid';
        this.tooltipNode.id = this._docToolTipTheme;
      },
    });
  }

  const util = ace.require('ace/autocomplete/util');
  const { Autocomplete, FilteredList } = ace.require('ace/autocomplete');
  const { AcePopup } = ace.require('./autocomplete/popup');
  // [SD] This is here to order/sort filtered completions groupBy meta tag.
  // picks top 3 keywords/builtIns and then columns, for tables we don't have
  // multiple completions
  (function () {
    const existingSetFilter = FilteredList.prototype.setFilter;
    this.setFilter = function (str) {
      // parse for aliases before constructing suggestions
      const customPrefixFilter =
        helper.isTableCompleter(this.filtered) ||
        this.filtered.some(({ suggestionType }) => suggestionType === 'python')
          ? str
          : helper.parseForAlias(str);
      // if user is typing @, we don't bother filtering as we will show first 20 definitions returned by algolia
      // https://app.asana.com/0/663129331545025/883556015876628/f
      if (str.includes('@')) {
        this.filtered = this.filtered.filter(({ suggestionType }) => suggestionType === 'definition');
        if (str.length > 1) {
          existingSetFilter.call(this, str.replace('@', ''));
        }
        // This assigment needs to be deffered so that suggestion works as expected.
        // Without setTimeout, filterText assignment will be synchronous and this causes autocompelete to not behave as expected
        setTimeout(() => (this.filterText = str), 0);
      } else {
        existingSetFilter.call(this, customPrefixFilter);
      }
      const groupedFiltered = groupBy(this.filtered, (item: AceAjax.Completion) => {
        const { suggestionType = '' } = item;
        return suggestionType;
      });
      if (groupedFiltered.hasOwnProperty('keywords')) {
        Object.assign(groupedFiltered, {
          keywords: groupedFiltered['keywords'].slice(0, 3),
        });
      }
      this.filtered = flatten((<any>Object).values(groupedFiltered));
    };
  }).call(FilteredList.prototype);

  function getCustomPopup(parentNode: HTMLElement): AceAjax.Editor {
    const originalPopup = new AcePopup(parentNode);
    // modified $tokenizeRow method to add an extra column to support icon
    // https://github.com/ajaxorg/ace/blob/master/lib/ace/autocomplete/popup.js#L172
    // Everything is same except an additional check on line 57 for icon
    function $tokenizeRow(row) {
      let data = originalPopup.data[row];
      const tokens = [];

      function addToken(value, className) {
        value &&
          tokens.push({
            type: (data.className || '') + (className || ''),
            value: value,
          });
      }

      if (!data) {
        return tokens;
      }
      if (typeof data === 'string') {
        data = { value: data };
      }
      const caption = data.caption || data.value || data.name;

      const lower = caption.toLowerCase();
      const filterText = (originalPopup.filterText || '').toLowerCase();
      let lastIndex = 0;
      let lastI = 0;
      for (let i = 0; i <= filterText.length; i++) {
        if (i !== lastI && (data.matchMask & (1 << i) || i === filterText.length)) {
          const sub = filterText.slice(lastI, i);
          lastI = i;
          const index = lower.indexOf(sub, lastIndex);
          if (index === -1) {
            continue;
          }
          addToken(caption.slice(lastIndex, index), '');
          lastIndex = index + sub.length;
          addToken(caption.slice(index, lastIndex), 'completion-highlight');
        }
      }
      addToken(caption.slice(lastIndex, caption.length), '');

      if (data.meta) {
        tokens.push({ type: 'completion-meta', value: data.meta });
      }

      if (data.icon) {
        tokens.push({ type: 'icon_' + data.icon, value: '' });
      }

      return tokens;
    }

    originalPopup.session.bgTokenizer.$tokenizeRow = $tokenizeRow;
    // applies ace_selected on hover'd row thus matches styling with keyboard
    originalPopup.setSelectOnHover(true);

    // [Suhas] This is only here until I know what I am doing wrong..
    // https://github.com/ajaxorg/ace/issues/3799
    originalPopup.renderer.on('afterRender', () => {
      const dom = ace.require('./lib/dom');
      const row = originalPopup.getRow();
      const t = originalPopup.renderer.$textLayer;
      const selected = t.element.childNodes[row - t.config.firstRow];
      if (selected !== t.selectedNode && t.selectedNode) {
        dom.removeCssClass(t.selectedNode, 'ace_selected');
      }
      t.selectedNode = selected;
      if (selected) {
        dom.addCssClass(selected, 'ace_selected');
      }
    });

    return originalPopup;
  }

  // inspired/copy+pasta from https://github.com/ajaxorg/ace/blob/master/lib/ace/ext/language_tools.js#L149
  return (event: any) => {
    const { editor, command } = event;
    const [theme] = editor.getTheme().split('/').slice(-1);

    const prefix = util.getCompletionPrefix(editor);
    if (!((command.name === 'backspace' || command.name === 'insertstring') && prefix !== '')) {
      return;
    }

    if (
      editor.preferences.autocomplete_mode === 'char_count' &&
      prefix.length < editor.preferences.autocomplete_mode_threshold
    ) {
      return;
    }

    if (!editor.completer) {
      // attach custom popup to Autocomplete's instance
      const autoComplete = configureDocTooltipTheme(new Autocomplete());
      const popup = getCustomPopup(document.body || document.documentElement);

      // popup object exactly like one constrcuted in $init https://github.com/ajaxorg/ace/blob/master/lib/ace/autocomplete.js#L64
      popup.on(
        'click',
        function (evt) {
          this.insertMatch();
          evt.stop();
        }.bind(autoComplete)
      );
      popup.on('select', autoComplete.tooltipTimer.bind(null, null));
      popup.on('show', autoComplete.tooltipTimer.bind(null, null));

      editor.completer = Object.assign(autoComplete, {
        popup,
      });
    }
    // Disable autoInsert
    editor.completer.autoInsert = false;
    editor.completer.updateDocTooltipTheme(theme);
    editor.completer.showPopup(editor);
  };
}
